import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  SearchIcon, 
  CheckIcon, 
  CrossIcon, 
  LockIcon, 
  CopyIcon, 
  ExternalLinkIcon, 
  RefreshIcon, 
  GlobeIcon, 
  ShieldIcon, 
  SparklesIcon,
  ChevronDownIcon,
  TagIcon
} from '../Components/Icons';
import { triggerHaptic } from '../Utils/miniApp';
import { scoreDomainRelevance, simplifySource, formatDomainPunycode } from '../Utils/hashtag';
import { setPageTitle } from '../Utils/brand';
import Footer from '../Components/Footer';
import { DomainCardItem, WhoisSkeletonCard } from './Home/SearchDOMAINS';
import ContactForm from '../Components/ContactForm';
import RentForm from '../Components/RentForm';
import ForwardForm from '../Components/ForwardForm';

export { formatDomainPunycode };

export default function DomainSearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawQ = searchParams.get('q') || '';
  // Sanitize initial query parameter against XSS
  const initialQuery = useMemo(() => {
    return rawQ.replace(/[<>"'`\\]/g, '').slice(0, 100).trim();
  }, [rawQ]);
  const navigate = useNavigate();

  const [inputVal, setInputVal] = useState(initialQuery);
  const [loading, setLoading] = useState(false);
  const [marketplaceLoading, setMarketplaceLoading] = useState(true);
  const [queueElapsed, setQueueElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [rawOpen, setRawOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [marketplaceDomains, setMarketplaceDomains] = useState([]);
  const [clientIp, setClientIp] = useState('');
  const [config, setConfig] = useState({
    whoisMode: 'full',
    enabled: true,
    partnerRegistratorUrl: 'https://nic.tg',
    turnstileSiteKey: '',
    rateLimitSeconds: 5
  });
  const [turnstileToken, setTurnstileToken] = useState('');

  const [selectedModalDomain, setSelectedModalDomain] = useState(null);
  const [activeModalTab, setActiveModalTab] = useState('buy');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const modalHasBuy = Boolean(selectedModalDomain?.pricebuy && Number(selectedModalDomain.pricebuy) > 0);
  const modalHasRent = Boolean(selectedModalDomain?.pricerent && Number(selectedModalDomain.pricerent) > 0);
  const modalHasForward = Boolean(selectedModalDomain?.pricefw && Number(selectedModalDomain.pricefw) > 0);

  const timerRef = useRef(null);
  const turnstileContainerRef = useRef(null);
  const inputRef = useRef(null);
  const inputValRef = useRef(inputVal);
  inputValRef.current = inputVal;
  const initialSearchRanRef = useRef(false);

  useEffect(() => {
    setPageTitle('Global Domain WHOIS Lookup');
    if (typeof window !== 'undefined' && window.innerWidth >= 768) {
      inputRef.current?.focus();
    }
  }, []);

  // Fetch whois config, client IP, and marketplace domains
  useEffect(() => {
    fetch('/api/whois/config')
      .then(res => res.json())
      .then(data => {
        if (data) {
          setConfig(prev => ({ ...prev, ...data }));
          if (data.clientIp) setClientIp(data.clientIp);
        }
      })
      .catch(() => {});

    fetch('/api/whois/my-ip')
      .then(res => res.json())
      .then(data => {
        if (data && data.ip) setClientIp(data.ip);
      })
      .catch(() => {});

    fetch('/api/domains')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setMarketplaceDomains(data);
      })
      .catch(() => {})
      .finally(() => setMarketplaceLoading(false));
  }, []);

  // Dynamically mount Turnstile when site key is available
  useEffect(() => {
    if (!config.turnstileSiteKey || !turnstileContainerRef.current) return;
    const renderWidget = () => {
      if (window.turnstile && turnstileContainerRef.current) {
        try {
          window.turnstile.render(turnstileContainerRef.current, {
            sitekey: config.turnstileSiteKey,
            callback: (token) => setTurnstileToken(token),
          });
        } catch (e) {
          // Widget already rendered
        }
      }
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      script.onload = renderWidget;
      document.body.appendChild(script);
    }
  }, [config.turnstileSiteKey]);

  const handleCheck = useCallback(async (domainToCheck) => {
    const rawVal = domainToCheck !== undefined ? domainToCheck : inputValRef.current;
    let raw = (rawVal || '').trim().replace(/^#/, '');
    if (!raw) {
      inputRef.current?.focus();
      return;
    }

    if (raw.length > 30) {
      setError('Domain name must not exceed 30 characters');
      triggerHaptic('error');
      return;
    }

    const parsed = formatDomainPunycode(raw);
    if (!parsed.isValid) {
      setError(parsed.error || 'Please enter a valid domain (e.g. startup.tg, brand.com, домен.tg)');
      triggerHaptic('error');
      return;
    }

    if (config.whoisMode === 'off' || !config.enabled) {
      setError('External registry domain lookup is currently disabled.');
      return;
    }

    triggerHaptic('light');
    setLoading(true);
    setError(null);
    setResult(null);
    setQueueElapsed(0);
    setRawOpen(false);

    setSearchParams({ q: parsed.unicode }, { replace: true });

    const startTime = Date.now();
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setQueueElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 500);

    try {
      const res = await fetch('/api/whois/check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          domain: parsed.punycode,
          turnstileToken
        })
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to check domain availability');
      }

      setResult({
        ...data,
        originalUnicode: parsed.unicode,
        isIdn: parsed.isIdn
      });
      triggerHaptic(data.isAvailable ? 'success' : 'warning');
    } catch (err) {
      const msg = err.message && !err.message.includes('Failed to fetch') && !err.message.includes('NetworkError')
        ? err.message
        : 'Unable to connect to registry. Please check your connection and try again.';
      setError(msg);
      triggerHaptic('error');
    } finally {
      if (timerRef.current) clearInterval(timerRef.current);
      setLoading(false);
    }
  }, [setSearchParams, turnstileToken, config.whoisMode, config.enabled]);

  // Initial trigger from URL param — executes strictly ONCE on mount
  useEffect(() => {
    if (initialQuery && !initialSearchRanRef.current) {
      initialSearchRanRef.current = true;
      handleCheck(initialQuery);
    }
  }, [initialQuery, handleCheck]);

  // Close WHOIS result and stay on the current whois page
  const handleCloseResult = useCallback(() => {
    setResult(null);
    setInputVal('');
    setError(null);
    setSearchParams({}, { replace: true });
    setTimeout(() => {
      inputRef.current?.focus();
    }, 30);
  }, [setSearchParams]);

  // Copy raw WHOIS to clipboard
  const handleCopyRaw = () => {
    if (!result?.whoisRaw) return;
    navigator.clipboard.writeText(result.whoisRaw).then(() => {
      setCopied(true);
      triggerHaptic('light');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Live Punycode & domain preview
  const previewDomain = useMemo(() => {
    return formatDomainPunycode(inputVal);
  }, [inputVal]);

  // Closest matching marketplace premium domains sorted by relevance (max 24 items, fast for 500+ domains)
  const { domains: relevantMarketplaceDomains, hasDirectMatches } = useMemo(() => {
    if (!marketplaceDomains || marketplaceDomains.length === 0) {
      return { domains: [], hasDirectMatches: false };
    }
    const q = inputVal.trim().toLowerCase().replace(/^#/, '');

    if (!q) {
      return { domains: marketplaceDomains.slice(0, 24), hasDirectMatches: false };
    }

    const scored = [];
    for (let i = 0; i < marketplaceDomains.length; i++) {
      const d = marketplaceDomains[i];
      const score = scoreDomainRelevance(d, q);
      if (score > 0) {
        scored.push({ domain: d, score });
      }
    }

    if (scored.length > 0) {
      scored.sort((a, b) => b.score - a.score);
      return {
        domains: scored.slice(0, 24).map(s => s.domain),
        hasDirectMatches: true
      };
    }

    return {
      domains: marketplaceDomains.slice(0, 24),
      hasDirectMatches: false
    };
  }, [marketplaceDomains, inputVal]);

  const isReadyToSearch = Boolean(inputVal.trim() && previewDomain.isValid && config.whoisMode !== 'off');

  return (
    <div className="min-h-screen flex flex-col justify-between">
      <div className="pt-24 pb-20 px-4 sm:px-6 max-w-5xl mx-auto w-full flex-1">
      {/* Hero Header */}
      <motion.div 
        initial={{ opacity: 0, y: -15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="text-center mb-8"
      >
        <div className="flex flex-wrap items-center justify-center gap-2 mb-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-sky-500/10 via-indigo-500/10 to-purple-500/10 border border-sky-500/20 text-sky-400 text-xs sm:text-sm font-semibold shadow-sm">
            <GlobeIcon className="w-4 h-4 text-sky-400" />
            <span>Global version</span>
          </div>
          {config.turnstileSiteKey && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-semibold">
              <ShieldIcon className="w-3.5 h-3.5" />
              <span>Bot Protection</span>
            </div>
          )}
        </div>
        <h1 className="text-3xl sm:text-5xl font-display font-extrabold text-white tracking-tight mb-3">
          Domain <span className="bg-gradient-to-r from-sky-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">WHOIS Lookup</span>
        </h1>
        <p className="text-neutral-400 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
          Lookup live registration details, RDAP records, DNS delegation status, and real-time availability across global and national registries.
        </p>
      </motion.div>

      {/* Mode Notice if disabled */}
      {config.whoisMode === 'off' && (
        <div className="max-w-2xl mx-auto mb-8 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm text-center">
          External registry domain check is currently turned off. Explore our exclusive marketplace domains below.
        </div>
      )}

      {/* Search Input Box */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35, delay: 0.1 }}
        className="relative max-w-3xl mx-auto mb-6"
      >
        <form 
          onSubmit={(e) => {
            e.preventDefault();
            handleCheck(inputVal);
          }}
          onClick={(e) => {
            if (e.target.tagName !== 'BUTTON' && !e.target.closest('button')) {
              inputRef.current?.focus();
            }
          }}
          className="relative flex items-center bg-dark-200/90 border border-white/10 hover:border-primary-500/40 focus-within:border-primary-500 rounded-2xl shadow-2xl backdrop-blur-xl transition-all p-1.5 sm:p-2 gap-1.5 sm:gap-2 cursor-text"
        >
          <div className="pl-2 sm:pl-3 text-neutral-400 shrink-0">
            <SearchIcon className="w-4 h-4 sm:w-5 sm:h-5 text-primary-400" />
          </div>

          <input
            ref={inputRef}
            type="text"
            maxLength={30}
            value={inputVal}
            onChange={(e) => {
              const val = e.target.value.slice(0, 30).replace(/\s{2,}/g, ' ');
              setInputVal(val);
              if (result && val.trim() !== (result.originalUnicode || result.domain)) {
                setResult(null);
              }
              if (error) setError(null);
            }}
            placeholder="Search domain (e.g. startup.tg, brand.com)..."
            className="flex-1 min-w-0 bg-transparent px-2 sm:px-3 py-2 sm:py-3 text-white placeholder-neutral-500 text-sm sm:text-base focus:outline-none"
          />

          {inputVal && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setInputVal('');
                setResult(null);
                setError(null);
                setSearchParams({}, { replace: true });
                inputRef.current?.focus();
              }}
              className="p-1.5 sm:p-2 text-neutral-400 hover:text-white transition-colors shrink-0"
              title="Clear"
            >
              <CrossIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </button>
          )}

          <button
            type="submit"
            onClick={(e) => {
              if (loading) return;
              if (!inputVal.trim()) {
                e.preventDefault();
                inputRef.current?.focus();
                triggerHaptic('light');
                return;
              }
              if (!previewDomain.isValid) {
                e.preventDefault();
                setError(previewDomain.error || 'Please enter a valid domain (e.g. startup.tg, brand.com)');
                inputRef.current?.focus();
                triggerHaptic('error');
                return;
              }
              if (config.whoisMode === 'off') {
                e.preventDefault();
                setError('External registry domain lookup is currently disabled.');
                return;
              }
            }}
            disabled={loading}
            className={`px-3.5 sm:px-6 md:px-8 py-2.5 sm:py-3.5 rounded-xl bg-gradient-to-r from-primary-500 to-indigo-600 hover:from-primary-600 hover:to-indigo-700 text-white font-semibold text-xs sm:text-sm md:text-base shadow-lg transition-all flex items-center justify-center gap-1.5 sm:gap-2 active:scale-95 shrink-0 ${
              !isReadyToSearch
                ? 'opacity-40 hover:opacity-60 cursor-pointer shadow-none'
                : 'shadow-primary-500/25 hover:scale-[1.02] cursor-pointer'
            }`}
            title={!inputVal.trim() ? "Click to enter domain name" : (!previewDomain.isValid ? previewDomain.error : "Search WHOIS")}
          >
            {loading ? (
              <>
                <RefreshIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin shrink-0" />
                <span>Checking...</span>
              </>
            ) : (
              <>
                <SearchIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                <span>WHOIS</span>
              </>
            )}
          </button>
        </form>

        {/* Live IDN Punycode Preview (shown only when non-Latin / IDN characters are typed) */}
        {previewDomain.isIdn && !loading && (
          <div className="text-right mt-2 px-3 text-xs text-neutral-400 font-mono flex items-center justify-end gap-1.5">
            <span className="text-neutral-500">IDN Punycode:</span>
            <span className="text-sky-400 font-semibold">{previewDomain.unicode}</span>
            <span className="text-neutral-600">→</span>
            <span className="text-neutral-300 font-normal">({previewDomain.punycode})</span>
          </div>
        )}
      </motion.div>

      {/* Client IP Address Badge (Responsive with horizontal scroll on small screens for long IPv6) */}
      <div className="max-w-3xl mx-auto flex items-center justify-center mb-8 px-2">
        <div className="max-w-full inline-flex items-center gap-2 sm:gap-2.5 px-3.5 sm:px-4 py-2 rounded-xl bg-dark-200/80 border border-white/10 text-xs sm:text-sm text-neutral-400 font-mono shadow-inner backdrop-blur-md overflow-x-auto no-scrollbar">
          <GlobeIcon className="w-4 h-4 text-sky-400 shrink-0" />
          <span className="text-neutral-400 shrink-0">Your IP Address:</span>
          <span className="inline-block min-w-[125px] text-left shrink-0">
            {clientIp ? (
              <span className="text-white font-semibold tracking-wider animate-fade-simple whitespace-nowrap">
                {clientIp}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-neutral-400 text-xs animate-fade-simple whitespace-nowrap">
                <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse" />
                <span>detecting...</span>
              </span>
            )}
          </span>
        </div>
      </div>

      {/* Cloudflare Turnstile Container if site key configured */}
      {config.turnstileSiteKey && (
        <div ref={turnstileContainerRef} className="flex justify-center mb-6" />
      )}

      {/* Unified Transition Slot: smoothly replaces Querying Registry with Results Card without layout shifts */}
      <AnimatePresence exitBeforeEnter mode="wait">
        {loading ? (
          <motion.div
            key="whois-loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            <WhoisSkeletonCard 
              queueElapsed={queueElapsed} 
              targetDomain={previewDomain?.unicode || inputVal} 
            />
          </motion.div>
        ) : error ? (
          <motion.div
            key="whois-error"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="max-w-2xl mx-auto p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm flex items-center gap-3 mb-8"
          >
            <CrossIcon className="w-5 h-5 flex-shrink-0 text-red-400" />
            <span>{error}</span>
          </motion.div>
        ) : result ? (
          <motion.div
            key="whois-result"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="max-w-3xl mx-auto mb-12"
          >
            {result.isAvailable ? (
              /* ================= AVAILABLE DOMAIN CARD ================= */
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-950/40 via-dark-100 to-dark-200 border-2 border-emerald-500/40 shadow-2xl p-6 sm:p-8">
                <div className="absolute -right-20 -top-20 w-64 h-64 bg-emerald-500/15 rounded-full blur-3xl pointer-events-none" />
                
                {/* Close WHOIS result button (icon only, stays on current page) */}
                <button
                  type="button"
                  onClick={handleCloseResult}
                  className="absolute top-4 right-4 sm:top-5 sm:right-5 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-neutral-400 hover:text-white border border-white/10 hover:border-white/30 flex items-center justify-center transition-all shadow-sm z-20 cursor-pointer active:scale-95"
                  title="Close"
                  aria-label="Close"
                >
                  <CrossIcon className="w-3.5 h-3.5" />
                </button>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 relative z-10 pr-12 sm:pr-14">
                  <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-xs font-bold uppercase tracking-wider mb-3">
                      <CheckIcon className="w-4 h-4" />
                      <span>Available for Registration</span>
                    </div>

                    <h2 className="text-3xl sm:text-4xl font-extrabold text-white font-display tracking-tight mb-2 flex flex-wrap items-baseline gap-2">
                      <span>{result.originalUnicode || result.domain}</span>
                      {result.isIdn && (
                        <span className="text-sm sm:text-base font-normal font-mono text-emerald-300/70">
                          ({result.domain})
                        </span>
                      )}
                    </h2>

                    <p className="text-emerald-300/90 text-sm sm:text-base font-medium max-w-md flex items-center gap-1.5">
                      <SparklesIcon className="w-5 h-5 text-emerald-400 shrink-0" />
                      <span>Great news! You can register this domain right now directly with the official registrar.</span>
                    </p>

                    {result.whoisMode === 'full' && result.checkedMethod && (
                      <div className="flex items-center gap-3 mt-4 text-xs text-neutral-400 font-mono">
                        <span>Source: {simplifySource(result.checkedMethod)}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-3 sm:items-end">
                    <a
                      href={result.partnerRegistratorUrl || 'https://nic.tg'}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => triggerHaptic('impact')}
                      className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold text-sm sm:text-base shadow-xl shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all active:scale-95 text-center"
                    >
                      <span>Register Domain Now</span>
                      <ExternalLinkIcon className="w-4 h-4" />
                    </a>

                    <span className="text-[11px] text-neutral-400 text-center sm:text-right">
                      Official Registrar Partner link
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              /* ================= REGISTERED DOMAIN CARD ================= */
              <div className="relative rounded-3xl bg-dark-100/90 border border-white/10 shadow-2xl p-6 sm:p-8 backdrop-blur-xl">
                {/* Close WHOIS result button (icon only, stays on current page) */}
                <button
                  type="button"
                  onClick={handleCloseResult}
                  className="absolute top-4 right-4 sm:top-5 sm:right-5 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-neutral-400 hover:text-white border border-white/10 hover:border-white/30 flex items-center justify-center transition-all shadow-sm z-20 cursor-pointer active:scale-95"
                  title="Close"
                  aria-label="Close"
                >
                  <CrossIcon className="w-3.5 h-3.5" />
                </button>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-6 border-b border-white/5 pr-12 sm:pr-14">
                  <div>
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-xs font-semibold mb-2">
                      <LockIcon className="w-3.5 h-3.5" />
                      <span>Domain Already Registered</span>
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-display flex flex-wrap items-baseline gap-2">
                      <span>{result.originalUnicode || result.domain}</span>
                      {result.isIdn && (
                        <span className="text-sm sm:text-base font-normal font-mono text-neutral-400">
                          ({result.domain})
                        </span>
                      )}
                    </h2>
                  </div>

                  {result.whoisMode === 'full' && (
                    <div className="text-xs text-neutral-400 font-mono text-left sm:text-right space-y-0.5">
                      {result.parsedDetails?.status && <div>Status: <span className="text-white font-semibold">{result.parsedDetails.status}</span></div>}
                      {result.checkedMethod && <div>Source: <span className="text-white font-semibold">{simplifySource(result.checkedMethod)}</span></div>}
                    </div>
                  )}
                </div>

                {/* Structured Metadata Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                  <div className="p-3.5 rounded-xl bg-dark-200/50 border border-white/5">
                    <div className="text-[11px] text-neutral-400 uppercase tracking-wider mb-1 font-semibold">Registrar</div>
                    <div className="text-white font-medium text-sm truncate">{result.parsedDetails?.registrar || 'N/A'}</div>
                  </div>

                  <div className="p-3.5 rounded-xl bg-dark-200/50 border border-white/5">
                    <div className="text-[11px] text-neutral-400 uppercase tracking-wider mb-1 font-semibold">Registration Date</div>
                    <div className="text-white font-medium text-sm">{result.parsedDetails?.activationDate || 'N/A'}</div>
                  </div>

                  <div className="p-3.5 rounded-xl bg-dark-200/50 border border-white/5">
                    <div className="text-[11px] text-neutral-400 uppercase tracking-wider mb-1 font-semibold">Expiration Date</div>
                    <div className="text-white font-medium text-sm">{result.parsedDetails?.expirationDate || 'N/A'}</div>
                  </div>
                </div>

                {/* Name Servers */}
                {result.parsedDetails?.nameServers?.length > 0 && (
                  <div className="mb-6 p-3.5 rounded-xl bg-dark-200/50 border border-white/5">
                    <div className="text-[11px] text-neutral-400 uppercase tracking-wider mb-2 font-semibold flex items-center gap-1.5">
                      <GlobeIcon className="w-3.5 h-3.5 text-primary-400" />
                      <span>Name Servers</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {result.parsedDetails.nameServers.map((ns, idx) => (
                        <span key={idx} className="px-2.5 py-1 rounded-md bg-dark-300 text-xs font-mono text-neutral-300 border border-white/5">
                          {ns}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Collapsible Raw WHOIS Record (only if whoisMode === 'full') */}
                {result.whoisRaw && config.whoisMode !== 'min' && (
                  <div className="border-t border-white/5 pt-4">
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => setRawOpen(prev => !prev)}
                        className="flex items-center gap-2 text-xs font-semibold text-neutral-300 hover:text-white transition-colors"
                      >
                        <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${rawOpen ? 'rotate-180' : ''}`} />
                        <span>{rawOpen ? 'Hide Full WHOIS Record' : 'Show Full WHOIS Record'}</span>
                      </button>

                      {rawOpen && (
                        <button
                          type="button"
                          onClick={handleCopyRaw}
                          className="flex items-center gap-1.5 text-xs text-primary-400 hover:text-primary-300 font-medium transition-colors"
                        >
                          <CopyIcon className="w-3.5 h-3.5" />
                          <span>{copied ? 'Copied!' : 'Copy RAW'}</span>
                        </button>
                      )}
                    </div>

                    {rawOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-3 p-4 rounded-xl bg-dark-300/80 border border-white/10 overflow-x-auto"
                      >
                        <pre className="text-xs font-mono text-neutral-300 leading-relaxed whitespace-pre-wrap select-all">
                          {result.whoisRaw}
                        </pre>
                      </motion.div>
                    )}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Relevant Premium Domains in Marketplace */}
      {marketplaceLoading ? (
        <div className="max-w-5xl mx-auto mt-14 pt-8 border-t border-white/10 mb-12 animate-fade-simple">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
            <div className="space-y-2">
              <div className="w-56 h-6 skeleton-shimmer rounded-lg" />
              <div className="w-80 h-3.5 skeleton-shimmer rounded" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 w-full">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-44 rounded-2xl bg-neutral-900/40 border border-white/[0.08] p-5 skeleton-shimmer" />
            ))}
          </div>
        </div>
      ) : relevantMarketplaceDomains.length > 0 ? (
        <div className="max-w-5xl mx-auto mt-14 pt-8 border-t border-white/10 mb-12 animate-fade-simple">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
            <div>
              <h3 className="text-lg sm:text-xl font-display font-bold text-white flex items-center gap-2">
                <TagIcon className="w-5 h-5 text-primary-400" />
                <span>Available Premium Marketplace Domains</span>
              </h3>
              <p className="text-xs text-neutral-400 mt-1">
                {inputVal.trim() 
                  ? (hasDirectMatches
                      ? `Ranked by relevance for "${inputVal.trim()}"`
                      : `No direct catalog match for "${inputVal.trim()}". Showing featured domains:`)
                  : 'Exclusive premium domains ready for immediate transfer or rental'}
              </p>
            </div>

            <Link
              to="/#domains"
              className="text-xs text-primary-400 hover:text-primary-300 font-semibold inline-flex items-center gap-1 transition-colors self-start sm:self-auto"
            >
              <span>View All Catalog →</span>
            </Link>
          </div>

          {/* Grid Layout for cards (same compact cards as in domain page carousel) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 w-full">
            {relevantMarketplaceDomains.map((item, idx) => (
              <div 
                key={item.id} 
                className="w-full flex animate-card-graceful"
                style={{ animationDelay: `${Math.min(idx * 28, 220)}ms` }}
              >
                <DomainCardItem
                  item={item}
                  compact={true}
                  setShowContactForm={() => {
                    setSelectedModalDomain(item);
                    setActiveModalTab('buy');
                    setIsModalOpen(true);
                  }}
                  setShowRentForm={() => {
                    setSelectedModalDomain(item);
                    setActiveModalTab('rent');
                    setIsModalOpen(true);
                  }}
                  setShowForwardForm={() => {
                    setSelectedModalDomain(item);
                    setActiveModalTab('forward');
                    setIsModalOpen(true);
                  }}
                  setSelectedDomain={setSelectedModalDomain}
                  onSelectTag={(tag) => {
                    setInputVal(`#${tag}`);
                    navigate(`/?q=%23${tag}#domains`);
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      ) : null}
      </div>

      {/* Domain Action Modals */}
      {selectedModalDomain && (
        <div
          className={isModalOpen ? "fixed inset-0 bg-black/85 backdrop-blur-xl flex items-center justify-center p-2.5 sm:p-6 z-50 overflow-y-auto modal-scrollbar animate-in fade-in duration-200" : "hidden"}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsModalOpen(false);
            }
          }}
        >
          {modalHasBuy && (
            <div className={activeModalTab === 'buy' ? "w-full max-w-lg md:max-w-xl flex justify-center" : "hidden"}>
              <ContactForm
                domain={selectedModalDomain}
                onClose={() => setIsModalOpen(false)}
                isPurchase={true}
                onSwitchToRent={modalHasRent ? () => setActiveModalTab('rent') : undefined}
                onSwitchToForward={modalHasForward ? () => setActiveModalTab('forward') : undefined}
              />
            </div>
          )}

          {modalHasRent && (
            <div className={activeModalTab === 'rent' ? "w-full max-w-lg md:max-w-xl flex justify-center" : "hidden"}>
              <RentForm
                domain={selectedModalDomain}
                onClose={() => setIsModalOpen(false)}
                onSwitchToBuy={modalHasBuy ? () => setActiveModalTab('buy') : undefined}
                onSwitchToForward={modalHasForward ? () => setActiveModalTab('forward') : undefined}
              />
            </div>
          )}

          {modalHasForward && (
            <div className={activeModalTab === 'forward' ? "w-full max-w-lg md:max-w-xl flex justify-center" : "hidden"}>
              <ForwardForm
                domain={selectedModalDomain}
                onClose={() => setIsModalOpen(false)}
                onSwitchToRent={modalHasRent ? () => setActiveModalTab('rent') : undefined}
                onSwitchToBuy={modalHasBuy ? () => setActiveModalTab('buy') : undefined}
              />
            </div>
          )}
        </div>
      )}

      <Footer />
    </div>
  );
}
